import React from 'react';
import type { FieldErrors } from 'react-hook-form';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { FormProgressStepper } from '~widgets/application-form/form-progress-stepper';

import { analytics } from '~entities/amplitude-analytics';

import {
  useCountries,
  useFormInitialize,
  useOnMountEffect,
} from '~shared/hooks';
import { combineValidators } from '~shared/lib/combine-validators';
import { getFieldsErrors } from '~shared/lib/get-fields-errors';
import { FormFooter } from '~shared/ui/form-footer';
import { HeaderWithLogo } from '~shared/ui/header-with-logo';
import { Screen } from '~shared/ui/screen';
import { ScreenTitle } from '~shared/ui/screen-title';
import { SectionTitle } from '~shared/ui/section-title';
import { validators } from '~shared/validators';

import { Input, Select } from '@breeze/rhf-adapters';

import { JobDetails } from './job-details';
import { UnemployedReason } from './unemployed-reason';

import { SELECT_REQUIRED_ERROR } from '../../constants/errors';
import {
  COUNTRY_OF_WORK_FIELD_NAME,
  EMPLOYMENT_STATUS_FIELD_NAME,
} from '../../constants/field-names';
import type {
  EmploymentStatusValue,
  NatureOfWorkValue,
  UnemployedReasonValue,
  WorkExperienceValue,
} from '../../lib';
import { employmentStatusOptions } from '../../lib/employment-status-options';

export interface FormValues {
  employmentStatus: EmploymentStatusValue;
  jobTitle?: string;
  natureOfWork?: NatureOfWorkValue;
  natureOfWorkOther?: string;
  workExperience?: WorkExperienceValue;
  unemployedReason?: UnemployedReasonValue;
  unemployedReasonOther?: string;
  country?: string;
  companyName?: string;
}

type Props = {
  initialValue?: Partial<FormValues>;
  onPrev: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  onSubmit: (values: FormValues) => void;
  onFieldCompleted?: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  applicationId?: string;
};

export const EmploymentDetails: React.FC<Props> = ({
  initialValue = {},
  onFieldCompleted,
  onPrev,
  onSubmit,
  applicationId,
}) => {
  const methods = useForm<FormValues>({
    mode: 'all',
    defaultValues: initialValue,
  });
  const { control, handleSubmit, getValues, watch } = methods;
  const [employmentStatus] = watch(['employmentStatus']);

  const { countries, isLoading: isCountriesLoading } = useCountries();

  useFormInitialize(methods, initialValue);

  useOnMountEffect(() => {
    if (applicationId) {
      analytics.track.ponApplicationEmploymentView(applicationId);
    }
  });

  const handleBlur = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onFieldCompleted?.(getValues(), actualErrors);
  };
  const handleClickButtonPrev = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onPrev(getValues(), actualErrors);
  };

  const handleFormSubmit = (values: FormValues) => {
    onSubmit(values);
    if (applicationId) {
      analytics.track.ponApplicationEmploymentNextClick(applicationId);
    }
  };

  return (
    <form onBlur={handleBlur} onSubmit={handleSubmit(handleFormSubmit)}>
      <Screen
        header={<HeaderWithLogo />}
        footer={<FormFooter onClickPrev={handleClickButtonPrev} />}
      >
        <div className="mb-5">
          <ScreenTitle
            title="What do you do for a&nbsp;living?"
            subtitle="Tell us about your main source of&nbsp;income"
            icon={<FormProgressStepper step="work" />}
          />
        </div>
        <FormProvider {...methods}>
          <Controller
            name="employmentStatus"
            control={control}
            rules={{
              required: 'Select one option',
            }}
            render={(fieldProps) => (
              <Select
                label={EMPLOYMENT_STATUS_FIELD_NAME}
                options={employmentStatusOptions}
                {...fieldProps}
              />
            )}
          />

          {employmentStatus === 'UNEMPLOYED' ? (
            <UnemployedReason />
          ) : (
            <JobDetails />
          )}

          {employmentStatus === 'OFW' && (
            <>
              <Controller
                name="country"
                control={control}
                rules={{ required: SELECT_REQUIRED_ERROR }}
                render={(fieldProps) => (
                  <Select
                    label={COUNTRY_OF_WORK_FIELD_NAME}
                    options={countries}
                    withSearch
                    loading={isCountriesLoading}
                    {...fieldProps}
                  />
                )}
              />
              <div className="flex flex-col items-stretch gap-5">
                <SectionTitle title="Company details" />
                <Controller
                  name="companyName"
                  control={control}
                  rules={{
                    validate: combineValidators(
                      validators.maxLength({ maxLength: 150 }),
                      validators.latinOnly()
                    ),
                  }}
                  render={(fieldProps) => (
                    <Input label="Company name (if any)" {...fieldProps} />
                  )}
                />
              </div>
            </>
          )}
        </FormProvider>
      </Screen>
    </form>
  );
};
